// Admin Profile Image Service - Handles uploading and syncing profile images across devices
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import app from '../config/firebase';

const storage = getStorage(app);

export class AdminProfileImageService {
  /**
   * Upload profile image to Firebase Storage
   * @param {File} imageFile - Image file from input
   * @param {string} userId - User ID for organizing storage
   * @returns {Promise<string>} - Cloud download URL
   */
  static async uploadProfileImage(imageFile: File, userId: string): Promise<string> {
    try {
      console.log('📤 AdminProfileImageService: Starting upload...', { 
        fileName: imageFile.name, 
        fileSize: imageFile.size,
        userId 
      });
      
      if (!imageFile || !userId) {
        throw new Error('Image file and User ID are required');
      }
      
      // Create storage reference with timestamp for uniqueness
      const timestamp = Date.now();
      const imagePath = `admin-profiles/${userId}/profile-${timestamp}.jpg`;
      const imageRef = ref(storage, imagePath);
      
      console.log('☁️ Uploading to Firebase Storage:', imagePath);
      
      // Upload to Firebase Storage
      const snapshot = await uploadBytes(imageRef, imageFile);
      console.log('✅ Upload completed, getting download URL...');
      
      // Get public download URL
      const downloadURL = await getDownloadURL(snapshot.ref);
      console.log('🔗 Admin profile image uploaded successfully:', downloadURL);
      
      return downloadURL;
    } catch (error: any) {
      console.error('❌ AdminProfileImageService upload failed:', error);
      
      // Provide more specific error messages
      if (error.code === 'storage/unauthorized') {
        throw new Error('You are not authorized to upload images. Please log in again.');
      } else if (error.code === 'storage/canceled') {
        throw new Error('Upload was canceled.');
      } else if (error.code === 'storage/unknown') {
        throw new Error('An unknown error occurred during upload.');
      } else {
        throw new Error(`Upload failed: ${error.message}`);
      }
    }
  }
  
  /**
   * Check if an image URL is a cloud URL (Firebase Storage) or local/blob
   * @param {string} imageUri - Image URI to check
   * @returns {boolean} - True if cloud URL, false if local
   */
  static isCloudImageUrl(imageUri: string | undefined | null): boolean {
    if (!imageUri) return false;
    
    return (
      imageUri.includes('firebasestorage.googleapis.com') ||
      imageUri.includes('storage.googleapis.com') ||
      imageUri.includes('.firebasestorage.app')
    );
  }
  
  /**
   * Get optimal image URI - prefer cloud over local
   * @param {Object} adminData - Admin data object
   * @returns {string|null} - Best available image URI
   */
  static getBestImageUri(adminData: any): string | null {
    const profileImage = adminData?.profileImage;
    
    if (this.isCloudImageUrl(profileImage)) {
      console.log('☁️ Using cloud admin profile image:', profileImage);
      return profileImage;
    }
    
    // If we have a blob URL or local URL, return it but log warning
    if (profileImage && !this.isCloudImageUrl(profileImage)) {
      console.log('📱 Using local admin profile image (will not sync):', profileImage);
      return profileImage;
    }
    
    console.log('ℹ️ No admin profile image available');
    return null;
  }
}

export default AdminProfileImageService;
